/**
 * Shared pax / Hubwayz lead / LDS proposal-ready shapes for unified flight+hotel.
 */

import {
  buildPaxListFromRoomInfo,
  createEmptyFlightPassengerSlot,
  DEFAULT_FLIGHT_PASSENGER_NATIONALITY,
} from "../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import {
  FLIGHTS_MODULE_PROPOSAL_ONLY_BODY_KEY,
  withFlightsModuleProposalOnly,
} from "../../config/flightsModuleLds";
import { isFlightPassengerDdMmYyyy } from "../../Components/FlightBookingFlowMockup/flightBookingMockup/flightPassengersFormShared";

/** Convert appliedSearch.rooms → roomInfo shape used by LDS pax helpers. */
export function roomInfoFromAppliedSearch(appliedSearch, paxFallback = 2) {
  const rooms = Array.isArray(appliedSearch?.rooms) ? appliedSearch.rooms : [];
  if (rooms.length) {
    return {
      rooms: rooms.map((r) => {
        const adultsRaw = r?.adults ?? r?.AdultsCount ?? r?.Adults;
        const adultsNum = Number(adultsRaw);
        const adults =
          Number.isFinite(adultsNum) && adultsNum >= 1 ? adultsNum : 1;
        const agesRaw = r?.childAges ?? r?.KidsAges ?? r?.kidsAges ?? [];
        const kidsAges = Array.isArray(agesRaw) ? agesRaw : [];
        return {
          adults,
          kids: kidsAges.length,
          infants: Number(r?.infants ?? r?.InfantsCount) || 0,
          kidsAges,
        };
      }),
    };
  }
  const fallback = Number(paxFallback);
  return {
    rooms: [
      {
        adults: Number.isFinite(fallback) && fallback >= 1 ? fallback : 2,
        kids: 0,
        infants: 0,
        kidsAges: [],
      },
    ],
  };
}

export function buildEmptyFlightPassengers(appliedSearch, paxFallback = 2) {
  const roomInfo = roomInfoFromAppliedSearch(appliedSearch, paxFallback);
  const slots = buildPaxListFromRoomInfo(roomInfo);
  return slots.map((slot, index) => ({
    ...createEmptyFlightPassengerSlot(index),
    id: slot.id,
    role: slot.role,
    labelHe: slot.labelHe,
    nationality: DEFAULT_FLIGHT_PASSENGER_NATIONALITY,
    isPrimary: index === 0,
  }));
}

/**
 * Hubwayz book: lead adult with contact (docs: one lead; contact on lead).
 * Primary contact passenger → hotel bookingRequest adults[0].
 */
export function mapPrimaryContactToHubwayzLead(passengers) {
  const list = Array.isArray(passengers) ? passengers : [];
  const lead = list.find((p) => p.isPrimary) || list[0];
  if (!lead) return null;
  const first = String(lead.firstName || "").trim();
  const last = String(lead.lastName || "").trim();
  if (!first || !last) return null;

  const title = String(lead.gender || lead.title || "M").toUpperCase() === "F" ? "F" : "M";
  const email = String(lead.email || "").trim();
  const phone = String(lead.cell || lead.phone || "").trim();
  const country = String(lead.nationality || "IL").trim().toUpperCase().slice(0, 2) || "IL";

  return {
    title,
    lead: true,
    name: { first, last },
    contact: {
      email: email || undefined,
      phone: phone || undefined,
      country,
      address: lead.address || "N/A",
      city: lead.city || "N/A",
      state: lead.state || "N/A",
      zip: lead.zip || "00000",
    },
  };
}

/**
 * Booking customer (docs: the person making the booking; not necessarily a guest).
 * birthDate is taken only from an explicit customer field, never from a guest DOB.
 * @param {object[]} passengers
 * @param {{ birthDate?: string }} [customerSource]
 */
export function mapPrimaryContactToHubwayzCustomer(passengers, customerSource = {}) {
  const lead = mapPrimaryContactToHubwayzLead(passengers);
  if (!lead) return null;
  const raw = String(customerSource?.birthDate || "").trim();
  const birthDate = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : ddMmYyyyToIso(raw);
  return {
    title: lead.title,
    name: { ...lead.name },
    contact: { ...lead.contact },
    ...(birthDate ? { birthDate } : {}),
  };
}

function ddMmYyyyToIso(raw) {
  const s = String(raw || "").trim();
  if (!isFlightPassengerDdMmYyyy(s)) return undefined;
  const [d, m, y] = s.split("/");
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

/**
 * Mishor /book body from Hubwayz prebook token + lead/customer adults.
 * Mishor schema (a205) requires searchRequest on each services[] item alongside token.
 * Payment: account_credit (agent billing / sandbox hold — bank transfer settles offline).
 */
export function buildHubwayzBookPayload({
  token,
  searchRequest,
  lead,
  customer,
  adults,
  clientRef,
}) {
  const t = String(token || "").trim();
  if (!t) return null;
  if (!searchRequest || typeof searchRequest !== "object") return null;
  const leadAdult = lead || (Array.isArray(adults) ? adults.find((a) => a?.lead) : null);
  if (!leadAdult?.name?.first || !leadAdult?.name?.last) return null;

  const roomsAdults = Array.isArray(adults) && adults.length ? adults : [leadAdult];

  // Mishor Book schema (a205): bookingRequest must be an array of room-booking objects.
  return {
    services: [
      {
        token: t,
        searchRequest,
        bookingRequest: [
          {
            clientRef: String(clientRef || `USM-${Date.now()}`).slice(0, 32),
            customer: customer || {
              title: leadAdult.title || "M",
              name: { ...leadAdult.name },
              contact: { ...(leadAdult.contact || {}) },
            },
            rooms: [{ adults: roomsAdults }],
          },
        ],
      },
    ],
    payment: { type: "account_credit" },
  };
}

/**
 * Marker payload for LDS flights_new first step = PROPOSAL only (no real BOOK).
 */
export function buildLdsProposalOnlyFlightSlice({
  rawDeal,
  passengers,
  extras,
  dealId,
}) {
  const flights = withFlightsModuleProposalOnly({
    dealId: dealId || rawDeal?.dealId || rawDeal?.ID || null,
    rawDeal: rawDeal || null,
    passengers: (passengers || []).map((p, i) => ({
      firstName: p.firstName,
      lastName: p.lastName,
      gender: p.gender,
      dateOfBirth: p.dateOfBirth,
      passportNumber: p.passportNumber,
      passportExpiry: p.passportExpiry,
      nationality: p.nationality,
      cell: p.cell,
      email: p.email,
      isPrimary: !!p.isPrimary || i === 0,
      _mockPaxId: p.id,
    })),
    extras: extras || { supplementPaxSelections: {} },
    [FLIGHTS_MODULE_PROPOSAL_ONLY_BODY_KEY]: true,
    validationMode: "quote_proposal",
  });
  return flights;
}
